import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { CapabilityRegistryEntry } from "@/services/capabilitiesApi";

interface CapabilityComboboxProps {
  capabilities: CapabilityRegistryEntry[];
  isLoading?: boolean;
  value: string | undefined;
  onValueChange: (capability: string | undefined) => void;
  /** When true, omit the "All capabilities" row (required pickers). */
  required?: boolean;
  placeholder?: string;
  className?: string;
}

export function CapabilityCombobox({
  capabilities,
  isLoading,
  value,
  onValueChange,
  required = false,
  placeholder = "All capabilities",
  className,
}: CapabilityComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const displayValue = value ?? "";

  const filtered = useMemo(() => {
    if (!search.trim()) return capabilities;
    const q = search.trim().toLowerCase();
    return capabilities.filter(
      (entry) =>
        entry.capability.toLowerCase().includes(q) ||
        entry.description.toLowerCase().includes(q),
    );
  }, [capabilities, search]);

  const handleSelect = (selected: string) => {
    if (selected === "__all__") {
      onValueChange(undefined);
    } else if (selected === value && !required) {
      onValueChange(undefined);
    } else {
      onValueChange(selected);
    }
    setSearch("");
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onValueChange(undefined);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setSearch("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("min-w-[200px] justify-between font-normal", className)}
        >
          <span className="truncate">{displayValue || placeholder}</span>
          <span className="flex items-center gap-1 shrink-0">
            {value && !required && (
              <X
                className="h-4 w-4 opacity-70 hover:opacity-100"
                aria-label="Clear capability"
                onClick={handleClear}
              />
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search capabilities..."
            className="h-9"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="max-h-[280px]">
            <CommandEmpty>
              {isLoading ? "Loading..." : "No capability found."}
            </CommandEmpty>
            <CommandGroup>
              {!required && (
                <CommandItem
                  value="__all__"
                  onSelect={() => handleSelect("__all__")}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      !value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  All capabilities
                </CommandItem>
              )}
              {filtered.map((entry) => (
                <CommandItem
                  key={entry.capability}
                  value={entry.capability}
                  onSelect={() => handleSelect(entry.capability)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === entry.capability ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate">{entry.capability}</span>
                    {entry.description ? (
                      <span className="truncate text-xs text-muted-foreground">
                        {entry.description}
                      </span>
                    ) : null}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
